import { apiFetch } from "./http";

export type StaffOperationalState =
  | "not_scheduled"
  | "scheduled"
  | "available"
  | "working"
  | "on_break"
  | "clocked_out";

export type StaffShiftSummary = {
  id: string;
  status: string;
  scheduledStart: string;
  scheduledEnd: string;
  timezone: string;
  startingLocation: string | null;
  operationalContext: unknown;
  roleName: string;
  eventName: string;
  hourlyRate: string | null;
  requestStatus: string | null;
  assignmentStatus: string | null;
  calledOut: boolean;
};

export type ShiftTimeRecord = {
  id: string;
  shiftAssignmentId: string;
  staffProfileId: string;
  scheduledStart: string;
  scheduledEnd: string;
  recordedClockIn: string | null;
  recordedClockOut: string | null;
  approvalStatus: string;
  earlyStartRequested: boolean;
};

export type PlannedBreak = {
  id: string;
  shiftAssignmentId: string;
  status: string;
  plannedStart: string | null;
  plannedEnd: string | null;
};

export type StaffShiftDetail = StaffShiftSummary & {
  timeRecord: ShiftTimeRecord | null;
  plannedBreaks: PlannedBreak[];
};

export type StaffMeBundle = {
  user: { id: string; name: string; email: string; administrativeRole: string; active: boolean };
  profile: {
    id: string;
    workerType: string;
    activeForStaffing: boolean;
    referenceCode: string | null;
  };
  qualifications: { id: string; operationalRoleId: string; roleName: string; active: boolean }[];
  rates: {
    id: string;
    operationalRoleId: string;
    roleName: string;
    hourlyRate: string;
    effectiveFrom: string;
    effectiveTo: string | null;
  }[];
};

export type StaffHome = {
  operationalState: StaffOperationalState;
  currentShift: {
    id: string;
    scheduledStart: string;
    scheduledEnd: string;
    startingLocation: string | null;
    status: string;
  } | null;
  nextShift: {
    id: string;
    scheduledStart: string;
    scheduledEnd: string;
    startingLocation: string | null;
    status: string;
  } | null;
  currentAssignment: { id: string; shiftId: string; status: string } | null;
  currentTask: WorkTask | null;
  nextTask: WorkTask | null;
};

export type WorkTask = {
  id: string;
  eventId: string;
  title: string;
  detail: string;
  status: string;
  scheduledTime: string | null;
  classification: string;
};

export type RecurringAvailabilityRow = {
  id: string;
  staffProfileId: string;
  dayOfWeek: number;
  available: boolean;
  startTime: string | null;
  endTime: string | null;
};

export type AvailabilityExceptionRow = {
  id: string;
  staffProfileId: string;
  exceptionDate: string;
  available: boolean;
  startTime: string | null;
  endTime: string | null;
  note: string | null;
};

export type StaffNotification = {
  id: string;
  userId: string;
  category: string;
  title: string;
  body: string;
  entityType: string | null;
  entityId: string | null;
  deepLink: string | null;
  readAt: string | null;
  createdAt: string;
};

export type NotificationPreference = {
  id: string;
  userId: string;
  category: string;
  inAppEnabled: boolean;
  pushEnabled: boolean;
};

export type CallOutReasonCode =
  | "sick"
  | "family_emergency"
  | "transportation_issue"
  | "schedule_conflict"
  | "other";

export const NOTIFICATION_CATEGORIES = [
  "available_shift",
  "shift_approved",
  "shift_changed",
  "shift_cancelled",
  "upcoming_shift",
  "break_reminder",
  "missed_punch",
  "time_correction",
  "call_out",
  "task_assignment",
  "management_announcement",
] as const;

export async function fetchStaffMe(): Promise<StaffMeBundle> {
  return apiFetch<StaffMeBundle>("/staff/me");
}

export async function fetchStaffHome(): Promise<StaffHome> {
  const res = await apiFetch<{ home: StaffHome }>("/staff/me/home");
  return res.home;
}

export async function fetchStaffAvailability(): Promise<{
  recurring: RecurringAvailabilityRow[];
  exceptions: AvailabilityExceptionRow[];
}> {
  return apiFetch("/staff/me/availability");
}

export type AvailabilityPatch = {
  recurring?: {
    dayOfWeek: number;
    available: boolean;
    startTime?: string | null;
    endTime?: string | null;
  }[];
  exception?: {
    exceptionDate: string;
    available: boolean;
    startTime?: string | null;
    endTime?: string | null;
    note?: string | null;
  };
  deleteExceptionId?: string;
};

export async function patchStaffAvailability(body: AvailabilityPatch) {
  return apiFetch<{ recurring: RecurringAvailabilityRow[]; exceptions: AvailabilityExceptionRow[] }>(
    "/staff/me/availability",
    { method: "PATCH", body: JSON.stringify(body) },
  );
}

export async function fetchAvailableShifts(): Promise<StaffShiftSummary[]> {
  const res = await apiFetch<{ items: StaffShiftSummary[] }>("/staff/shifts/available");
  return res.items;
}

export async function fetchStaffShifts(filter: "upcoming" | "past" | "all"): Promise<StaffShiftSummary[]> {
  const res = await apiFetch<{ items: StaffShiftSummary[] }>(`/staff/shifts?filter=${filter}`);
  return res.items;
}

export async function fetchStaffShift(id: string): Promise<StaffShiftDetail> {
  const res = await apiFetch<{ shift: StaffShiftDetail }>(`/staff/shifts/${id}`);
  return res.shift;
}

export async function requestShift(id: string) {
  return apiFetch<{ request: { id: string; status: string } }>(`/staff/shifts/${id}/request`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function callOutShift(id: string, body: { reasonCode: CallOutReasonCode; reasonNote?: string }) {
  return apiFetch<{ ok: boolean }>(`/staff/shifts/${id}/call-out`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function clockInShift(id: string, earlyStartRequested?: boolean) {
  return apiFetch(`/staff/shifts/${id}/clock-in`, {
    method: "POST",
    body: JSON.stringify({ earlyStartRequested }),
  });
}

export async function clockOutShift(id: string) {
  return apiFetch(`/staff/shifts/${id}/clock-out`, { method: "POST", body: JSON.stringify({}) });
}

export async function startBreakShift(id: string) {
  return apiFetch(`/staff/shifts/${id}/break/start`, { method: "POST", body: JSON.stringify({}) });
}

export async function endBreakShift(id: string) {
  return apiFetch(`/staff/shifts/${id}/break/end`, { method: "POST", body: JSON.stringify({}) });
}

export async function fetchStaffWork(): Promise<{ currentTask: WorkTask | null; nextTask: WorkTask | null }> {
  return apiFetch("/staff/work");
}

export async function fetchStaffNotifications(category?: string): Promise<StaffNotification[]> {
  const q = category ? `?category=${encodeURIComponent(category)}` : "";
  const res = await apiFetch<{ items: StaffNotification[] }>(`/staff/notifications${q}`);
  return res.items;
}

export async function markNotificationRead(id: string) {
  return apiFetch(`/staff/notifications/${id}/read`, { method: "POST", body: JSON.stringify({}) });
}

export async function fetchNotificationPreferences(): Promise<NotificationPreference[]> {
  const res = await apiFetch<{ preferences: NotificationPreference[] }>("/staff/notification-preferences");
  return res.preferences;
}

export async function putNotificationPreferences(
  preferences: { category: string; inAppEnabled: boolean; pushEnabled: boolean }[],
) {
  const res = await apiFetch<{ preferences: NotificationPreference[] }>("/staff/notification-preferences", {
    method: "PUT",
    body: JSON.stringify({ preferences }),
  });
  return res.preferences;
}

export async function submitTimeCorrection(body: {
  shiftTimeRecordId: string;
  correctionType: string;
  proposedValue: Record<string, unknown>;
  staffExplanation: string;
}) {
  return apiFetch(`/staff/time-corrections`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}
